import { getRecommendation, normalizeRecommendationTiers } from './scoreConfig';
import { CONTACTED_STATUSES, DEFAULT_STATUS } from './submissionStatus';

const DAY_MS = 24 * 60 * 60 * 1000;

export const scorePct = (pointEval) => {
  const max = Number(pointEval?.maxScore) || 0;
  if (max <= 0) return null;
  return Math.round(((Number(pointEval?.totalScore) || 0) / max) * 100);
};

export const filterByRange = (subs, days, now = new Date()) => {
  if (days === 'all' || !days) return subs;
  const cutoff = now.getTime() - days * DAY_MS;
  return subs.filter(s => s.submittedAt instanceof Date && s.submittedAt.getTime() >= cutoff);
};

export const filterByQuestionnaire = (items, questionnaireId) => {
  if (!questionnaireId || questionnaireId === 'all') return items;
  return items.filter(i => i.questionnaireId === questionnaireId);
};

export const NO_TEMPLATE = 'Ohne Vorlage';

// Resolve an item's questionnaire → its template name. Questionnaires without a
// template_name all collapse into a single "Ohne Vorlage" bucket (NOT their
// individual title), so template-grouping stays clean.
export const templateOf = (item, questionnairesById) => {
  const q = questionnairesById?.[item.questionnaireId];
  return (q?.templateName && String(q.templateName).trim()) || NO_TEMPLATE;
};

export const filterByTemplate = (items, questionnairesById, templateName) => {
  if (!templateName || templateName === 'all') return items;
  return items.filter(i => templateOf(i, questionnairesById) === templateName);
};

// Distinct template names across the questionnaires, sorted, with "Ohne Vorlage"
// (untemplated) always last.
export const templateOptions = (questionnairesById) => {
  const set = new Set();
  Object.values(questionnairesById || {}).forEach(q => {
    set.add((q?.templateName && String(q.templateName).trim()) || NO_TEMPLATE);
  });
  const list = Array.from(set);
  const named = list.filter(t => t !== NO_TEMPLATE).sort((a, b) => a.localeCompare(b));
  return set.has(NO_TEMPLATE) ? [...named, NO_TEMPLATE] : named;
};

export const filterTokensByCreated = (tokens, days, now = new Date()) => {
  if (days === 'all' || !days) return tokens;
  const cutoff = now.getTime() - days * DAY_MS;
  return tokens.filter(t => t.createdAt instanceof Date && t.createdAt.getTime() >= cutoff);
};

const median = (nums) => {
  const arr = nums.filter(n => typeof n === 'number' && !Number.isNaN(n)).sort((a, b) => a - b);
  if (arr.length === 0) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : Math.round((arr[mid - 1] + arr[mid]) / 2);
};

export const medianDuration = (subs) =>
  median(subs.map(s => s.completionDuration).filter(d => typeof d === 'number' && d > 0));

export const computeKpis = (subs, tokens, tiers) => {
  const sent = tokens.length;
  // "Ergebnisse" = every filled-out questionnaire, straight from the submissions
  // collection (same source as the Ergebnisse page). We do NOT read the token's
  // status/used_at (only written by the admin flow, flat-zero otherwise).
  const submitted = subs.length;
  const conversionPct = sent > 0 ? Math.round((submitted / sent) * 100) : null;

  const topLabel = normalizeRecommendationTiers(tiers)[0].label;
  const scored = subs.map(s => scorePct(s.point_evaluation)).filter(p => p !== null);
  const avgScorePct = scored.length
    ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length) : null;
  const recommended = subs.filter(s => {
    const p = scorePct(s.point_evaluation);
    return p !== null && getRecommendation(p, tiers).label === topLabel;
  }).length;
  const recommendedPct = scored.length ? Math.round((recommended / scored.length) * 100) : null;

  const untouched = subs.filter(s => (s.status || 'Unbearbeitet') === 'Unbearbeitet').length;
  const untouchedPct = subs.length ? Math.round((untouched / subs.length) * 100) : null;

  return {
    sent, submitted, conversionPct,
    evaluatedCount: scored.length, totalCount: subs.length,
    avgScorePct, recommendedPct, untouchedPct
  };
};

export const pctDelta = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
};

export const scoreDistribution = (subs, tiers, binSize = 10) => {
  const bins = [];
  for (let start = 0; start < 100; start += binSize) {
    const end = start + binSize;
    bins.push({
      start, end, label: `${start}–${end}`, count: 0,
      color: getRecommendation(start + binSize / 2, tiers).color
    });
  }
  subs.forEach(s => {
    const p = scorePct(s.point_evaluation);
    if (p === null) return;
    let idx = Math.floor(p / binSize);
    if (idx >= bins.length) idx = bins.length - 1; // 100 lands in the last bin
    if (idx < 0) idx = 0;
    bins[idx].count += 1;
  });
  return bins;
};

export const recommendationSplit = (subs, tiers) => {
  const list = normalizeRecommendationTiers(tiers);
  const counts = new Map(list.map(t => [t.label, 0]));
  let evaluated = 0;
  subs.forEach(s => {
    const p = scorePct(s.point_evaluation);
    if (p === null) return;
    evaluated += 1;
    const label = getRecommendation(p, tiers).label;
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return list.map(t => ({
    label: t.label, color: t.color,
    count: counts.get(t.label) || 0,
    pct: evaluated ? Math.round(((counts.get(t.label) || 0) / evaluated) * 100) : 0
  }));
};

// Shared row builder for the comparison table. `groups` is a Map of
// key → { title, list }; produces one sorted row per group.
// A buggy timer occasionally recorded runaway durations (hours). By default we
// drop anything over this cap from the time stats so median/avg/max stay real;
// callers can pass { ignoreOutliers: false } to include everything.
export const DURATION_OUTLIER_CAP_SEC = 2 * 60 * 60; // 2 h

const buildComparisonRows = (groups, tiers, { ignoreOutliers = true } = {}) => {
  const topLabel = normalizeRecommendationTiers(tiers)[0].label;
  const rows = [];
  groups.forEach(({ title, list }, id) => {
    const scored = list.map(s => scorePct(s.point_evaluation)).filter(p => p !== null);
    const recommended = list.filter(s => {
      const p = scorePct(s.point_evaluation);
      return p !== null && getRecommendation(p, tiers).label === topLabel;
    }).length;
    const durations = list.map(s => s.completionDuration).filter(d =>
      typeof d === 'number' && d > 0 && (!ignoreOutliers || d <= DURATION_OUTLIER_CAP_SEC));
    rows.push({
      id,
      title,
      count: list.length,
      evaluatedCount: scored.length,
      avgScorePct: scored.length ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length) : null,
      recommendedPct: scored.length ? Math.round((recommended / scored.length) * 100) : null,
      medianDurationSec: median(durations),
      avgDurationSec: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null,
      minDurationSec: durations.length ? Math.min(...durations) : null,
      maxDurationSec: durations.length ? Math.max(...durations) : null,
      durationCount: durations.length,       // how many results fed the time stats
      ignoredCount: list.map(s => s.completionDuration)
        .filter(d => typeof d === 'number' && d > 0).length - durations.length,
      split: recommendationSplit(list, tiers) // Einladen/Unsicher/Ablehnung breakdown
    });
  });
  return rows.sort((a, b) => b.count - a.count);
};

export const perQuestionnaireStats = (subs, questionnairesById, tiers, options) => {
  const groups = new Map();
  subs.forEach(s => {
    const id = s.questionnaireId || 'unknown';
    if (!groups.has(id)) groups.set(id, { title: questionnairesById?.[id]?.title || 'Unbenannter Fragebogen', list: [] });
    groups.get(id).list.push(s);
  });
  return buildComparisonRows(groups, tiers, options);
};

// Groups submissions by the questionnaire's template_name; untemplated ones all
// collapse into a single "Ohne Vorlage" bucket (via templateOf). Collapses the
// per-campaign clones that share a template into a single row.
export const perTemplateStats = (subs, questionnairesById, tiers, options) => {
  const groups = new Map();
  subs.forEach(s => {
    const key = templateOf(s, questionnairesById);
    if (!groups.has(key)) groups.set(key, { title: key, list: [] });
    groups.get(key).list.push(s);
  });
  return buildComparisonRows(groups, tiers, options);
};

const stripHtml = (s) => String(s || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

const isAnswered = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim() !== '';
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') {
    if ('answer' in val) return isAnswered(val.answer);              // image polymorphic
    if ('fileIds' in val) return Array.isArray(val.fileIds) && val.fileIds.length > 0;
    return Object.keys(val).length > 0;
  }
  return true;
};

export const normalizeText = (s) => String(s).trim().toLowerCase().replace(/\s+/g, ' ');

export const topAnswers = (values, n = 5) => {
  const counts = new Map();
  const display = new Map();
  values.forEach(v => {
    const key = normalizeText(v);
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
    if (!display.has(key)) display.set(key, String(v).trim());
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([key, count]) => ({ value: display.get(key), count }));
};

export const perQuestionAnswerStats = (subs, questionnaire) => {
  const questions = [...(questionnaire?.defaultQuestions || []), ...(questionnaire?.questions || [])];
  const total = subs.length;
  return questions.map(q => {
    const raw = subs.map(s => s.answers?.[q.id]);
    const answered = raw.filter(isAnswered);
    const answeredCount = answered.length;
    const result = {
      id: q.id,
      text: stripHtml(q.text),
      type: q.type,
      answeredCount,
      skippedCount: total - answeredCount,
      skipRatePct: total ? Math.round(((total - answeredCount) / total) * 100) : 0,
      distribution: [],
      ranking: [],
      textAnswers: [],
      topAnswers: []
    };
    // matrix/combobox answers are arrays of { label, value } rows; the chosen
    // `value`s come from q.options — aggregate them like a multiple-choice pick.
    const isRowBased = q.type === 'matrix' || q.type === 'combobox';
    if (q.type === 'single-choice' || q.type === 'multiple-choice' || isRowBased) {
      const counts = new Map((q.options || []).map(o => [o, 0]));
      answered.forEach(val => {
        const picks = isRowBased
          ? (Array.isArray(val) ? val : []).map(r => r && r.value).filter(Boolean)
          : (Array.isArray(val) ? val : [val]);
        picks.forEach(pick => counts.set(pick, (counts.get(pick) || 0) + 1));
      });
      result.multiple = q.type === 'multiple-choice' || isRowBased;
      result.distribution = Array.from(counts.entries())
        .map(([option, count]) => ({
          option, count,
          pct: answeredCount ? Math.round((count / answeredCount) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count);
    } else if (q.type === 'ranking') {
      // ranking answers are arrays where position = priority (index 0 = rank 1)
      // and each entry is an option index (or label). Aggregate the average rank.
      const options = Array.isArray(q.options) ? q.options : [];
      const agg = new Map();
      answered.forEach(val => {
        (Array.isArray(val) ? val : []).forEach((raw, idx) => {
          const ni = Number(raw);
          let label = (Number.isInteger(ni) && ni >= 0 && ni < options.length)
            ? options[ni]
            : (typeof raw === 'string' ? raw : '');
          label = String(label || '').trim();
          if (!label) return;
          const a = agg.get(label) || { sum: 0, count: 0 };
          a.sum += idx + 1; a.count += 1;
          agg.set(label, a);
        });
      });
      result.ranking = Array.from(agg.entries())
        .map(([option, a]) => ({ option, avgRank: Math.round((a.sum / a.count) * 10) / 10, count: a.count }))
        .sort((x, y) => x.avgRank - y.avgRank);
    } else if (q.type === 'text') {
      const texts = answered.map(v => String(v).trim()).filter(Boolean);
      result.textAnswers = texts;
      result.topAnswers = topAnswers(texts, 5);
    }
    return result;
  });
};

// Buckets by UTC day intentionally: PocketBase stores timestamps in UTC, and
// keying on UTC keeps day-bucketing deterministic across test/CI timezones.
// Trade-off: a token created 00:00–02:00 Europe/Berlin lands in the prior
// calendar day of the trend chart. Acceptable for a daily trend; do NOT switch
// to local getDate() without an explicit timezone conversion (it would make the
// buckets runner-timezone-dependent and break the tests).
const dateKey = (d) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

export const sentVsSubmittedOverTime = (tokens, subs, days, now = new Date()) => {
  const span = (days === 'all' || !days) ? null : days;
  const buckets = new Map();
  // Normalize start to midnight UTC of the earliest day in the span
  const todayMidnightUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const start = span ? new Date(todayMidnightUtc - (span - 1) * DAY_MS) : null;
  if (span) {
    for (let i = 0; i < span; i++) {
      buckets.set(dateKey(new Date(start.getTime() + i * DAY_MS)), { sent: 0, submitted: 0 });
    }
  }
  const inRange = (d) => !start || d.getTime() >= start.getTime();
  const bump = (d, field) => {
    const k = dateKey(d);
    if (!buckets.has(k)) buckets.set(k, { sent: 0, submitted: 0 });
    buckets.get(k)[field] += 1;
  };
  // "sent" = one questionnaire link generated (token.createdAt).
  tokens.forEach(t => {
    if (t.createdAt instanceof Date && inRange(t.createdAt)) bump(t.createdAt, 'sent');
  });
  // "submitted" = every filled-out questionnaire, keyed by its submission date.
  // Straight from the submissions collection (same rows the Ergebnisse page
  // shows) — no token/used_at dependency.
  (subs || []).forEach(s => {
    if (s.submittedAt instanceof Date && inRange(s.submittedAt)) bump(s.submittedAt, 'submitted');
  });
  return Array.from(buckets.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, v]) => ({ date, label: date.slice(5), sent: v.sent, submitted: v.submitted }));
};

export const statusFlow = (subs) => {
  const count = (st) => subs.filter(s => (s.status || DEFAULT_STATUS) === st).length;
  const nUnbearbeitet = count('Unbearbeitet');
  const nInKontakt = count('In Kontakt');
  const nInterview = count('Interview');
  const nProbestunde = count('Probestunde');
  const nZurueck = count('Bewerbung zurückgezogen');
  const nAbsage = count('Absage');
  const nZusage = count('Zusage');
  // Everyone who got past "Unbearbeitet" without withdrawing was contacted.
  const kontaktiert = CONTACTED_STATUSES.reduce((sum, st) => sum + count(st), 0);
  const total = subs.length;

  const nodes = [
    { name: 'Ergebnisse' },    // 0
    { name: 'Kontaktiert' },   // 1
    { name: 'Unbearbeitet' },  // 2
    { name: 'Zurückgezogen' }, // 3
    { name: 'In Kontakt' },    // 4 — contacted, before an Interview or Probestunde
    { name: 'Zusage' },        // 5
    { name: 'Absage' },        // 6
    { name: 'Interview' },     // 7
    { name: 'Probestunde' }    // 8
  ];
  const rawLinks = [
    { source: 0, target: 1, value: kontaktiert },
    { source: 0, target: 2, value: nUnbearbeitet },
    { source: 0, target: 3, value: nZurueck },
    { source: 1, target: 4, value: nInKontakt },
    { source: 1, target: 7, value: nInterview },
    { source: 1, target: 8, value: nProbestunde },
    { source: 1, target: 5, value: nZusage },
    { source: 1, target: 6, value: nAbsage }
  ].filter(l => l.value > 0);

  // Prune orphan nodes and remap indices so recharts <Sankey> is happy.
  const used = new Set();
  rawLinks.forEach(l => { used.add(l.source); used.add(l.target); });
  const remap = new Map();
  const prunedNodes = [];
  nodes.forEach((node, idx) => {
    if (used.has(idx)) { remap.set(idx, prunedNodes.length); prunedNodes.push(node); }
  });
  const links = rawLinks.map(l => ({
    source: remap.get(l.source), target: remap.get(l.target), value: l.value
  }));

  return {
    nodes: prunedNodes,
    links,
    untouchedPct: total ? Math.round((nUnbearbeitet / total) * 100) : 0,
    hired: nZusage,
    total
  };
};
